import { useCallback, useEffect, useState } from "react";
import api, { isVersionConflict } from "../../lib/api";
import { formatDateTime, formatTime, isoToLocalInput, localInputToIso } from "../../lib/missions";
import {
  ACTION_LABELS, ACTIVITIES, ACTIVITY_LABELS, ANOMALY_LABELS, SOURCE_LABELS, TYPE_LABELS, attendanceErrorMessage, canChange, canRecordFor, eventsOf,
  formatMinutes, myEntry, totalsText,
} from "../../lib/attendance";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Skeleton } from "../ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, ChevronDown, Clock, Loader2, Pencil, UserPlus, XCircle } from "lucide-react";

const SAVED_LABELS = { arrival: "Arrivée enregistrée", break_start: "Pause enregistrée", break_end: "Reprise enregistrée", departure: "Départ enregistré" };
const newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}-${Math.random().toString(16).slice(2)}`);

// « Temps » card of a mission: the signed-in person points from their phone with one big button; organisers record for
// others, correct with a reason and validate. Everything shown comes from GET /missions/{id}/attendance (rights in `capabilities`).
export default function MissionTimeCard({ mission, userId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null); // {kind: "record"|"correct"|"void"|"validate"|"reopen", event?}

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/missions/${mission.id}/attendance`);
      setData(d);
      setError("");
    } catch (e) {
      setError(attendanceErrorMessage(e, "Impossible de charger les temps."));
    }
  }, [mission.id]);
  useEffect(() => { load(); }, [load, mission.status]);

  if (error && !data) {
    return <Card className="p-5 text-sm text-slate-600 flex items-center gap-2 flex-wrap" data-testid="mission-time">{error}<Button variant="outline" size="sm" onClick={load}>Réessayer</Button></Card>;
  }
  if (!data) return <Card className="p-5"><Skeleton className="h-16 w-full" /></Card>;

  const caps = data.capabilities;
  const me = myEntry(data, userId);
  const canPointSelf = me && canRecordFor(data, me.person);
  const others = caps.record_for.filter((p) => !(p.kind === "user" && p.id === userId));
  const validated = data.review.status === "validated";
  const close = () => setDialog(null);
  const done = (d, message) => { setData(d); close(); if (message) toast.success(message); };

  return (
    <Card className="p-5 space-y-4" data-testid="mission-time">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Clock size={16} className="text-slate-400" />Temps</h3>
        <div className="flex items-center gap-2">
          {validated && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800" data-testid="mission-time-validated"><CheckCircle2 size={12} />Validés</span>}
          {others.length > 0 && <Button variant="outline" size="sm" onClick={() => setDialog({ kind: "record" })} data-testid="mission-time-record-for"><UserPlus size={14} className="mr-1.5" />Pointer pour…</Button>}
        </div>
      </div>

      {canPointSelf && <PointNow mission={mission} entry={me} onDone={(d, m) => done(d, m)} />}

      {data.people.length === 0 ? <p className="text-sm text-slate-500">Personne dans l'équipe pour le moment.</p> : (
        <ul className="divide-y divide-slate-100">
          {data.people.map((entry) => (
            <PersonRow key={`${entry.person.kind}:${entry.person.id}`} entry={entry} data={data} userId={userId} timeZone={mission.timezone}
              onCorrect={(event) => setDialog({ kind: "correct", event })} onVoid={(event) => setDialog({ kind: "void", event })} />
          ))}
        </ul>
      )}

      {data.people.some((p) => p.periods.length) && <p className="text-sm text-slate-700" data-testid="mission-time-total">Total de l'équipe : {totalsText(data.totals)}</p>}

      {validated ? (
        <div className="text-xs text-slate-500 flex items-center justify-between gap-2 flex-wrap">
          <span>Validés le {formatDateTime(data.review.validated_at, mission.timezone)} par {data.review.validated_by_name}.</span>
          {caps.reopen && <Button variant="outline" size="sm" onClick={() => setDialog({ kind: "reopen" })} data-testid="mission-time-reopen">Rouvrir</Button>}
        </div>
      ) : caps.validate && (
        <Button variant="outline" onClick={() => setDialog({ kind: "validate" })} data-testid="mission-time-validate">Valider les temps</Button>
      )}
      {data.review.reopened.length > 0 && (
        <ul className="text-xs text-slate-500 space-y-0.5">
          {data.review.reopened.map((r) => <li key={r.at}>Rouverts le {formatDateTime(r.at, mission.timezone)} par {r.by_name} : {r.reason}</li>)}
        </ul>
      )}

      <RecordDialog open={dialog?.kind === "record"} onOpenChange={close} mission={mission} persons={others} onDone={(d) => done(d, "Pointage enregistré")} />
      <CorrectDialog open={dialog?.kind === "correct"} onOpenChange={close} mission={mission} event={dialog?.event} onDone={(d) => done(d, "Pointage corrigé")} />
      <ReasonDialog open={dialog?.kind === "void"} onOpenChange={close} title="Annuler ce pointage ?" testid="mission-time-void-dialog"
        description="Il ne comptera plus, mais restera visible avec le motif." confirm="Annuler le pointage" danger
        url={dialog?.event ? `/missions/${mission.id}/attendance/${dialog.event.id}/void` : null} body={(reason) => ({ reason })}
        onDone={(d) => done(d, "Pointage annulé")} />
      <ReasonDialog open={dialog?.kind === "reopen"} onOpenChange={close} title="Rouvrir les temps ?" testid="mission-time-reopen-dialog"
        description="Ils pourront de nouveau être corrigés. Le motif reste dans l'historique." confirm="Rouvrir"
        url={`/missions/${mission.id}/attendance/reopen`} body={(reason) => ({ expected_version: data.review.version, reason })}
        onDone={(d) => done(d, "Temps rouverts")} onConflict={load} />
      <ValidateDialog open={dialog?.kind === "validate"} onOpenChange={close} mission={mission} data={data} onDone={(d) => done(d, "Temps validés")} onConflict={load} />
    </Card>
  );
}

// One big button for what the person does now; the nature (sur place / trajet / préparation) is chosen at arrival.
function PointNow({ mission, entry, onDone }) {
  const [activity, setActivity] = useState("on_site");
  const [late, setLate] = useState(false);
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(null);
  const arriving = entry.next_types[0] === "arrival";
  const open = entry.open_period;

  const send = async (type) => {
    setBusy(type);
    try {
      const body = { type, activity: arriving ? activity : open.activity };
      if (late && time) body.event_at = localInputToIso(time, mission.timezone);
      const { data } = await api.post(`/missions/${mission.id}/attendance`, body, { headers: { "Idempotency-Key": newKey() } });
      setLate(false);
      setTime("");
      onDone(data, SAVED_LABELS[type]);
    } catch (e) {
      toast.error(attendanceErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="mission-time-point">
      <p className="text-sm text-slate-700" data-testid="mission-time-my-state">
        {open ? `${open.on_break ? "En pause" : ACTIVITY_LABELS[open.activity]} depuis ${formatTime(open.start, mission.timezone)}` : "Vous n'êtes pas pointé."}
        {entry.totals.total > 0 && <span className="text-slate-500"> · {formatMinutes(entry.totals.total)} au total</span>}
      </p>
      {arriving && (
        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Nature du temps">
          {ACTIVITIES.map((a) => (
            <button key={a} type="button" role="radio" aria-checked={activity === a} onClick={() => setActivity(a)} data-testid={`mission-time-activity-${a}`}
              className={`h-10 rounded-lg border text-sm font-medium ${activity === a ? "border-jamora bg-jamora-50 text-jamora" : "border-slate-200 bg-white text-slate-700"}`}>
              {ACTIVITY_LABELS[a]}
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        {entry.next_types.map((type, index) => (
          <Button key={type} onClick={() => send(type)} disabled={!!busy || (late && !time)} data-testid={`mission-time-${type}`}
            className="h-12 flex-1 text-base" variant={index === 0 ? "default" : "outline"}>
            {busy === type && <Loader2 size={16} className="mr-2 animate-spin" />}{ACTION_LABELS[type]}
          </Button>
        ))}
      </div>
      <button type="button" className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" aria-expanded={late}
        onClick={() => { setLate((v) => !v); setTime(isoToLocalInput(new Date().toISOString(), mission.timezone)); }} data-testid="mission-time-late-toggle">
        <ChevronDown size={12} className={late ? "rotate-180" : ""} />Indiquer l'heure réelle
      </button>
      {late && (
        <div className="space-y-1.5">
          <Label htmlFor="mission-time-at" className="text-xs">Heure réelle</Label>
          <Input id="mission-time-at" type="datetime-local" className="text-base md:text-sm max-w-xs" value={time} onChange={(e) => setTime(e.target.value)} data-testid="mission-time-at" />
        </div>
      )}
    </div>
  );
}

function PersonRow({ entry, data, userId, timeZone, onCorrect, onVoid }) {
  const [open, setOpen] = useState(entry.anomalies.length > 0);
  const events = eventsOf(data, entry.person);
  const { person } = entry;
  return (
    <li className="py-3" data-testid={`mission-time-person-${person.kind}-${person.id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="w-full flex items-start justify-between gap-3 text-left">
        <div className="min-w-0">
          <div className="font-medium text-slate-900 break-words">{person.name}{person.kind === "collaborator" && <span className="text-xs font-normal text-slate-500"> · sans compte CRM</span>}{!entry.in_team && <span className="text-xs font-normal text-slate-500"> · retiré de l'équipe</span>}</div>
          <div className="text-xs text-slate-500" data-testid={`mission-time-totals-${person.id}`}>{totalsText(entry.totals)}{entry.open_period ? " · en cours" : ""}</div>
          {entry.anomalies.length > 0 && (
            <div className="text-xs text-amber-800 flex items-center gap-1 mt-0.5" data-testid={`mission-time-anomalies-${person.id}`}>
              <AlertTriangle size={12} />{[...new Set(entry.anomalies.map((a) => ANOMALY_LABELS[a.code] || a.code))].join(" · ")}
            </div>
          )}
        </div>
        <ChevronDown size={15} className={`text-slate-400 shrink-0 mt-1 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ol className="mt-2 space-y-1.5" data-testid={`mission-time-events-${person.id}`}>
          {events.length === 0 && <li className="text-xs text-slate-500">Aucun pointage.</li>}
          {events.filter((e) => e.type !== "void").map((e) => {
            const gone = e.state !== "active";
            const voided = e.state === "voided" ? data.events.find((x) => x.corrects === e.id) : null;
            return (
              <li key={e.id} className="flex items-start justify-between gap-2 text-sm" data-testid={`mission-time-event-${e.id}`}>
                <div className={`min-w-0 ${gone ? "text-slate-400" : "text-slate-800"}`}>
                  <span className={gone ? "line-through" : ""}>{formatTime(e.event_at, timeZone)} · {TYPE_LABELS[e.type]}{e.type === "arrival" ? ` (${ACTIVITY_LABELS[e.activity].toLowerCase()})` : ""}</span>
                  <div className="text-xs text-slate-500">
                    {formatDateTime(e.event_at, timeZone).slice(0, 10)} · {SOURCE_LABELS[e.source]}{e.source !== "self" ? ` (${e.recorded_by_name})` : ""}
                    {e.reason && ` · corrigé : ${e.reason}`}{e.state === "replaced" && " · remplacé"}{voided && ` · annulé : ${voided.reason}`}
                  </div>
                </div>
                {canChange(data, e, userId) && (
                  <div className="flex shrink-0">
                    <Button variant="ghost" size="icon" aria-label="Corriger ce pointage" onClick={() => onCorrect(e)} data-testid={`mission-time-correct-${e.id}`}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="icon" aria-label="Annuler ce pointage" onClick={() => onVoid(e)} data-testid={`mission-time-void-${e.id}`}><XCircle size={14} /></Button>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </li>
  );
}

function useSubmit(onDone, onConflict) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (request) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await request();
      onDone(data);
    } catch (e) {
      setError(attendanceErrorMessage(e));
      if (isVersionConflict(e)) onConflict?.();
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, submit };
}

function DialogError({ text }) {
  return text ? <p role="alert" className="text-sm text-red-700" data-testid="mission-time-dialog-error">{text}</p> : null;
}

function RecordDialog({ open, onOpenChange, mission, persons, onDone }) {
  const [person, setPerson] = useState("");
  const [type, setType] = useState("arrival");
  const [activity, setActivity] = useState("on_site");
  const [time, setTime] = useState("");
  const [key, setKey] = useState(newKey);
  const req = useSubmit(onDone);
  useEffect(() => {
    if (!open) return;
    setPerson(persons.length === 1 ? `${persons[0].kind}:${persons[0].id}` : "");
    setType("arrival"); setActivity("on_site"); setTime(isoToLocalInput(new Date().toISOString(), mission.timezone)); setKey(newKey()); req.setError("");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const [kind, id] = person.split(":");
  const send = () => req.submit(() => api.post(`/missions/${mission.id}/attendance`,
    { person: { kind, id }, type, activity, event_at: localInputToIso(time, mission.timezone) }, { headers: { "Idempotency-Key": key } }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mission-time-record-dialog">
        <DialogHeader>
          <DialogTitle>Pointer pour une personne de l'équipe</DialogTitle>
          <DialogDescription>Vous resterez enregistré comme auteur de la saisie.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Personne</Label>
            <Select value={person} onValueChange={setPerson}>
              <SelectTrigger data-testid="mission-time-record-person"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>{persons.map((p) => <SelectItem key={`${p.kind}:${p.id}`} value={`${p.kind}:${p.id}`}>{p.name}{p.kind === "collaborator" ? " (sans compte)" : ""}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Pointage</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="mission-time-record-type"><SelectValue /></SelectTrigger>
                <SelectContent>{["arrival", "break_start", "break_end", "departure"].map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Nature</Label>
              <Select value={activity} onValueChange={setActivity}>
                <SelectTrigger data-testid="mission-time-record-activity"><SelectValue /></SelectTrigger>
                <SelectContent>{ACTIVITIES.map((a) => <SelectItem key={a} value={a}>{ACTIVITY_LABELS[a]}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="space-y-1.5"><Label htmlFor="mission-time-record-at">Heure</Label>
            <Input id="mission-time-record-at" type="datetime-local" className="text-base md:text-sm" value={time} onChange={(e) => setTime(e.target.value)} data-testid="mission-time-record-at" /></div>
          <DialogError text={req.error} />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={req.busy}>Annuler</Button>
          <Button onClick={send} disabled={req.busy || !person || !time}  data-testid="mission-time-record-confirm">
            {req.busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CorrectDialog({ open, onOpenChange, mission, event, onDone }) {
  const [time, setTime] = useState("");
  const [activity, setActivity] = useState("on_site");
  const [reason, setReason] = useState("");
  const req = useSubmit(onDone);
  useEffect(() => {
    if (!open || !event) return;
    setTime(isoToLocalInput(event.event_at, mission.timezone)); setActivity(event.activity); setReason(""); req.setError("");
  }, [open, event]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!event) return null;
  const send = () => req.submit(() => api.post(`/missions/${mission.id}/attendance/${event.id}/correct`,
    { event_at: localInputToIso(time, mission.timezone), activity, reason: reason.trim() }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mission-time-correct-dialog">
        <DialogHeader>
          <DialogTitle>Corriger : {TYPE_LABELS[event.type]} de {event.person.name}</DialogTitle>
          <DialogDescription>L'ancien pointage ({formatDateTime(event.event_at, mission.timezone)}) reste visible ; le temps est recalculé.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label htmlFor="mission-time-correct-at">Heure exacte</Label>
            <Input id="mission-time-correct-at" type="datetime-local" className="text-base md:text-sm" value={time} onChange={(e) => setTime(e.target.value)} data-testid="mission-time-correct-at" /></div>
          {event.type === "arrival" && (
            <div className="space-y-1.5"><Label>Nature</Label>
              <Select value={activity} onValueChange={setActivity}>
                <SelectTrigger data-testid="mission-time-correct-activity"><SelectValue /></SelectTrigger>
                <SelectContent>{ACTIVITIES.map((a) => <SelectItem key={a} value={a}>{ACTIVITY_LABELS[a]}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <div className="space-y-1.5"><Label htmlFor="mission-time-correct-reason">Motif *</Label>
            <Textarea id="mission-time-correct-reason" rows={2} maxLength={300} className="text-base md:text-sm" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="mission-time-correct-reason" /></div>
          <DialogError text={req.error} />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={req.busy}>Annuler</Button>
          <Button onClick={send} disabled={req.busy || !time || reason.trim().length < 3} className="bg-jamora hover:bg-jamora-hover text-white" data-testid="mission-time-correct-confirm">
            {req.busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Corriger
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReasonDialog({ open, onOpenChange, title, description, confirm, danger, url, body, onDone, onConflict, testid }) {
  const [reason, setReason] = useState("");
  const req = useSubmit(onDone, onConflict);
  useEffect(() => { if (open) { setReason(""); req.setError(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{description}</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <Label htmlFor={`${testid}-reason`}>Motif *</Label>
          <Textarea id={`${testid}-reason`} rows={2} maxLength={300} className="text-base md:text-sm" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`${testid}-reason`} />
          <DialogError text={req.error} />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={req.busy}>Fermer</Button>
          <Button onClick={() => req.submit(() => api.post(url, body(reason.trim())))} disabled={req.busy || reason.trim().length < 3} data-testid={`${testid}-confirm`}
            className={danger ? "bg-red-600 hover:bg-red-700 text-white" : undefined}>
            {req.busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ValidateDialog({ open, onOpenChange, mission, data, onDone, onConflict }) {
  const req = useSubmit(onDone, onConflict);
  useEffect(() => { if (open) req.setError(""); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const anomalies = data.people.reduce((n, p) => n + p.anomalies.length, 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mission-time-validate-dialog">
        <DialogHeader>
          <DialogTitle>Valider les temps de la mission ?</DialogTitle>
          <DialogDescription>Ils seront figés. Une correction demandera ensuite de les rouvrir avec un motif.</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-slate-700">Total : {totalsText(data.totals)}.</p>
        {anomalies > 0 && <p className="text-sm text-amber-800 flex items-center gap-1.5"><AlertTriangle size={14} />{anomalies} anomalie{anomalies > 1 ? "s" : ""} restent signalée{anomalies > 1 ? "s" : ""} : vérifiez-les avant de valider.</p>}
        <DialogError text={req.error} />
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={req.busy}>Annuler</Button>
          <Button onClick={() => req.submit(() => api.post(`/missions/${mission.id}/attendance/validate`, { expected_version: data.review.version }))}
            disabled={req.busy}  data-testid="mission-time-validate-confirm">
            {req.busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Valider
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
